import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminEditCourse() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    courseName: '',
    courseSummary: '',
    coursePeriod: '',
    fees: ''
  });
  const [courseImage, setCourseImage] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  // Validate form fields
  const isFormValid = () => {
    return (
      formData.courseName.trim().length >= 1 &&
      formData.courseSummary.trim().length >= 1 &&
      formData.coursePeriod.trim().length >= 1 &&
      formData.fees.trim() !== '' &&
      !isNaN(parseFloat(formData.fees)) &&
      parseFloat(formData.fees) >= 0
    );
  };

  useEffect(() => {
    const fetchCourse = async () => {
      setFetchLoading(true);
      setMessage('');
      setMessageType('');
      try {
        const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
        if (!sessionData || !sessionData.token) {
          setMessage('You must be logged in to edit a course.');
          setMessageType('error');
          setFetchLoading(false);
          return;
        }

        const response = await axios.get(`${apiUrl}/api/admin/courses/${id}`, {
          headers: {
            Authorization: `Bearer ${sessionData.token}`
          }
        });

        if (response.data.success) {
          const course = response.data.course;
          setFormData({
            courseName: course.coursename || '',
            courseSummary: course.coursesummary || '',
            coursePeriod: course.courseduration || '',
            fees: course.fees ? course.fees.toString() : ''
          });
        } else {
          setMessage(response.data.message || 'Failed to fetch course details.');
          setMessageType('error');
        }
      } catch (err) {
        console.error('Error fetching course:', err);
        setMessage(
          err.response?.data?.message ||
          'Error fetching course details. Please check your network or server configuration.'
        );
        setMessageType('error');
      } finally {
        setFetchLoading(false);
      }
    };

    fetchCourse();
  }, [id]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB = 5 * 1024 * 1024 bytes)
      if (file.size > 5 * 1024 * 1024) {
        setMessage('Image size must be under 5MB.');
        setMessageType('error');
        return;
      }
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setMessage('Image must be a JPEG, JPG, PNG, or GIF.');
        setMessageType('error');
        return;
      }
    }
    setCourseImage(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setMessageType('');

    if (!apiUrl) {
      setMessage('API URL is not defined. Please check your environment configuration.');
      setMessageType('error');
      setLoading(false);
      return;
    }

    if (!isFormValid()) {
      setMessage('Please fill in all required fields with valid values.');
      setMessageType('error');
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append('courseName', formData.courseName.trim());
    data.append('courseSummary', formData.courseSummary.trim());
    data.append('coursePeriod', formData.coursePeriod.trim());
    data.append('fees', formData.fees.trim());
    if (courseImage) {
      data.append('courseImage', courseImage);
    }

    // Log FormData contents for debugging
    console.log('FormData contents:');
    for (let [key, value] of data.entries()) {
      console.log(`${key}: ${value instanceof File ? value.name : value}`);
    }

    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setMessage('You must be logged in to update a course.');
        setMessageType('error');
        setLoading(false);
        return;
      }

      const response = await axios.put(`${apiUrl}/api/admin/courses/${id}`, data, {
        headers: {
          Authorization: `Bearer ${sessionData.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setMessage('Course updated successfully!');
        setMessageType('success');
        setTimeout(() => navigate('/admin-view-course'), 2000);
      } else {
        setMessage(response.data.message || 'Failed to update course.');
        setMessageType('error');
      }
    } catch (err) {
      console.error('Client error:', {
        message: err.message,
        code: err.code,
        response: err.response ? {
          status: err.response.status,
          data: err.response.data
        } : null
      });
      setMessage(
        err.response?.data?.message ||
        err.code === 'ERR_NETWORK'
          ? 'Network error: Unable to reach the server. Please check if the server is running and CORS is configured correctly.'
          : 'Error updating course. Please try again.'
      );
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link
                    to="/"
                    className="text-gray-200 fw-normal text-15 hover-text-main-600"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <Link
                    to="/admin-view-course"
                    className="text-gray-200 fw-normal text-15 hover-text-main-600"
                  >
                    Courses
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Edit Course</span>
                </li>
              </ul>
            </div>
            <div className="flex-align justify-content-end gap-8">
        
            </div>
          </div>
          <ul className="step-list mb-24">
            <li className="step-list__item py-15 px-24 text-15 text-heading fw-medium flex-center gap-6 active">
              <span className="icon text-xl d-flex">
                <i className="" />
              </span>
              <h3>Course Details</h3>
              <span className="line position-relative" />
            </li>
          </ul>
          <div className="card">
            <div className="card-header border-bottom border-gray-100 flex-align gap-8">
              <h5 className="mb-0">Edit Course</h5>
              <button
                type="button"
                className="text-main-600 text-md d-flex"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-title="Edit Course Details"
              >
                <i className="ph-fill ph-question" />
              </button>
            </div>
            <div className="card-body">
              {fetchLoading && (
                <div className="alert alert-info mb-20" role="alert">
                  Loading course details...
                </div>
              )}
              {message && (
                <div className={`alert alert-${messageType === 'success' ? 'success' : 'danger'} mb-20`} role="alert">
                  {message}
                </div>
              )}
              {!fetchLoading && (
                <form id="editCourseForm" onSubmit={handleSubmit}>
                  <div className="row gy-20">
                    <div className="col-xxl-12">
                      <h6 className="mb-20 fw-semibold font-heading">
                        Course Information
                      </h6>
                      <div className="row g-20">
                        <div className="col-sm-6">
                          <label
                            htmlFor="courseName"
                            className="h5 mb-8 fw-semibold font-heading"
                          >
                            Course Name{" "}
                            <span className="text-13 text-red-500 fw-medium">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control py-11"
                            id="courseName"
                            placeholder="Enter Course Name"
                            value={formData.courseName}
                            onChange={handleInputChange}
                            required
                            minLength="1"
                            disabled={loading}
                          />
                        </div>
                        <div className="col-sm-6">
                          <label
                            htmlFor="courseImage"
                            className="h5 mb-8 fw-semibold font-heading"
                          >
                            Course Image{" "}
                            <span className="text-13 text-gray-500 fw-medium">(Optional)</span>
                          </label>
                          <input
                            type="file"
                            className="form-control py-11"
                            id="courseImage"
                            accept="image/jpeg,image/jpg,image/png,image/gif"
                            onChange={handleImageChange}
                            disabled={loading}
                          />
                        </div>
                        <div className="col-sm-12">
                          <label
                            htmlFor="courseSummary"
                            className="h5 mb-8 fw-semibold font-heading"
                          >
                            Course Summary{" "}
                            <span className="text-13 text-red-500 fw-medium">*</span>
                          </label>
                          <textarea
                            className="form-control py-11"
                            id="courseSummary"
                            placeholder="Enter Course Summary"
                            rows="5"
                            value={formData.courseSummary}
                            onChange={handleInputChange}
                            required
                            minLength="1"
                            disabled={loading}
                          ></textarea>
                        </div>
                        <div className="col-sm-6">
                          <label
                            htmlFor="coursePeriod"
                            className="h5 mb-8 fw-semibold font-heading"
                          >
                            Course Period{" "}
                            <span className="text-13 text-red-500 fw-medium">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control py-11"
                            id="coursePeriod"
                            placeholder="Enter Course Period (e.g., 6 months)"
                            value={formData.coursePeriod}
                            onChange={handleInputChange}
                            required
                            minLength="1"
                            disabled={loading}
                          />
                        </div>
                        <div className="col-sm-6">
                          <label
                            htmlFor="fees"
                            className="h5 mb-8 fw-semibold font-heading"
                          >
                            Fees{" "}
                            <span className="text-13 text-red-500 fw-medium">*</span>
                          </label>
                          <input
                            type="number"
                            className="form-control py-11"
                            id="fees"
                            placeholder="Enter Course Fees"
                            value={formData.fees}
                            onChange={handleInputChange}
                            required
                            min="0"
                            step="0.01"
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-align justify-content-end gap-8">
                      <Link
                        to="/admin-view-course"
                        className="btn btn-outline-main rounded-pill py-9"
                      >
                        Cancel
                      </Link>
                      <button
                        type="submit"
                        className="btn btn-main rounded-pill py-9"
                        disabled={loading || !isFormValid()}
                      >
                        {loading ? 'Updating...' : 'Update Course'}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminEditCourse;